import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { formatPersonalNumber } from '../utils/personalNumber'
import { isValidPhoneNumber } from '../utils/phoneNumber'
import { isValidPersonalNumberFormat } from '@estercare/ester-shared'

interface FormData {
  personalNumber: string
  phoneNumber: string
}

interface ValidationErrors {
  personalNumber: string
  phoneNumber: string
}

interface UseHealthCallFormValidationProps {
  initialValues?: Partial<FormData>
}

export const useHealthCallFormValidation = ({ initialValues = {} }: UseHealthCallFormValidationProps = {}) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<FormData>({
    personalNumber: initialValues.personalNumber || '',
    phoneNumber: initialValues.phoneNumber || '',
  })

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    personalNumber: '',
    phoneNumber: '',
  })

  const [touchedFields, setTouchedFields] = useState<Set<keyof FormData>>(new Set())

  const personalNumberTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const phoneNumberTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!touchedFields.has('personalNumber')) return

    if (personalNumberTimeoutRef.current) {
      clearTimeout(personalNumberTimeoutRef.current)
    }

    personalNumberTimeoutRef.current = setTimeout(() => {
      if (!formData.personalNumber) {
        setValidationErrors((prev) => ({ ...prev, personalNumber: '' }))
      } else if (!isValidPersonalNumberFormat(formData.personalNumber)) {
        setValidationErrors((prev) => ({
          ...prev,
          personalNumber: t('employee.healthCall.personalNumberValidationError'),
        }))
      } else {
        setValidationErrors((prev) => ({ ...prev, personalNumber: '' }))
      }
    }, 2000)

    // eslint-disable-next-line consistent-return
    return () => {
      if (personalNumberTimeoutRef.current) {
        clearTimeout(personalNumberTimeoutRef.current)
      }
    }
  }, [formData.personalNumber, touchedFields, t])

  useEffect(() => {
    if (!touchedFields.has('phoneNumber')) return

    if (phoneNumberTimeoutRef.current) {
      clearTimeout(phoneNumberTimeoutRef.current)
    }

    // Set new timeout for validation after 2 seconds
    phoneNumberTimeoutRef.current = setTimeout(() => {
      if (!formData.phoneNumber) {
        setValidationErrors((prev) => ({ ...prev, phoneNumber: '' }))
      } else if (!isValidPhoneNumber(formData.phoneNumber)) {
        setValidationErrors((prev) => ({
          ...prev,
          phoneNumber: t('employee.healthCall.phoneNumberValidationError'),
        }))
      } else {
        setValidationErrors((prev) => ({ ...prev, phoneNumber: '' }))
      }
    }, 2000)

    // eslint-disable-next-line consistent-return
    return () => {
      if (phoneNumberTimeoutRef.current) {
        clearTimeout(phoneNumberTimeoutRef.current)
      }
    }
  }, [formData.phoneNumber, touchedFields, t])

  const handlePersonalNumberChange = useCallback((value: string) => {
    const formatted = formatPersonalNumber(value)
    setFormData((prev) => ({ ...prev, personalNumber: formatted }))
    setValidationErrors((prev) => ({ ...prev, personalNumber: '' }))
    setTouchedFields((prev) => new Set(prev).add('personalNumber'))
  }, [])

  const handlePhoneNumberChange = useCallback((value: string) => {
    // Ensure +46 prefix is always present
    if (!value.startsWith('+46')) {
      value = '+46'
    }
    setFormData((prev) => ({ ...prev, phoneNumber: value }))
    setValidationErrors((prev) => ({ ...prev, phoneNumber: '' }))
    setTouchedFields((prev) => new Set(prev).add('phoneNumber'))
  }, [])

  const validateForm = useCallback((): boolean => {
    const errors: ValidationErrors = {
      personalNumber: '',
      phoneNumber: '',
    }
    let isValid = true

    if (!formData.personalNumber) {
      errors.personalNumber = t('employee.healthCall.personalNumberRequired')
      isValid = false
    } else if (!isValidPersonalNumberFormat(formData.personalNumber)) {
      errors.personalNumber = t('employee.healthCall.personalNumberValidationError')
      isValid = false
    }

    if (!formData.phoneNumber) {
      errors.phoneNumber = t('employee.healthCall.phoneNumberRequired')
      isValid = false
    } else if (!isValidPhoneNumber(formData.phoneNumber)) {
      errors.phoneNumber = t('employee.healthCall.phoneNumberValidationError')
      isValid = false
    }

    setValidationErrors(errors)
    return isValid
  }, [formData, t])

  const isFormValid = useMemo(
    () =>
      !!formData.personalNumber &&
      isValidPersonalNumberFormat(formData.personalNumber) &&
      !!formData.phoneNumber &&
      isValidPhoneNumber(formData.phoneNumber),
    [formData]
  )

  const resetForm = useCallback(() => {
    setFormData({
      personalNumber: initialValues.personalNumber || '',
      phoneNumber: initialValues.phoneNumber || '',
    })
    setValidationErrors({
      personalNumber: '',
      phoneNumber: '',
    })
    setTouchedFields(new Set())
  }, [initialValues])

  const setFormValues = useCallback((values: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...values }))
  }, [])

  const setFieldError = useCallback((field: keyof ValidationErrors, error: string) => {
    setValidationErrors((prev) => ({ ...prev, [field]: error }))
  }, [])

  return {
    formData,
    validationErrors,
    isFormValid,

    handlePersonalNumberChange,
    handlePhoneNumberChange,

    validateForm,
    resetForm,
    setFormValues,
    setFieldError,

    isValidPersonalNumberFormat,
    isValidPhoneNumber,
  }
}
